// ---------------------------------------------------------------------------
// telephonicService
//
// Company-scoped access to telephonic encounter calls for the
// end-user Hub. These calls live in the `Conversation` collection with
// `isTelephonic == true` and a denormalized `company_id` (resolved from the
// dialed TelephonyAccessCodes PIN). They carry no `user_id` and no patient,
// so they never come back from the per-user `getUserSessions` query the
// Encounters tab runs — and a regular user can't read them directly from
// Firestore under the client rules either.
//
// Both calls therefore go through the backend (Admin SDK), which enforces the
// company scope server-side:
//   - list:   GET /api/v1/telephonic/conversations   (new, company-scoped)
//   - detail: GET /api/v1/conversation?callId=...     (existing; returns the
//             call + its lowercase `messages` transcript, company-checked)
// ---------------------------------------------------------------------------

import {request as apiRequest} from './apiClient';

// Mirrors adminService.buildAdminQuery: drops empty values, stringifies the
// rest into a leading-`?` query string.
function buildQuery(params: Record<string, unknown> = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// Telephonic routes return the full envelope (callers read payload.data and
// payload.pagination), so we keep unwrap:false.
const request = (
  path: string,
  options: import('./apiClient').RequestOptions = {},
) => apiRequest(path, {...options, unwrap: false});

export const telephonicService = {
  /**
   * Paginated list of the caller's company's telephonic calls, newest first.
   * Returns { conversations, nextCursor, hasMore }. `nextCursor` is the ISO
   * start_time of the last row; pass it back as `cursor` for the next page.
   */
  async getCompanyTelephonicConversations({
    cursor = null,
    limit = 25,
    startDate = null,
    endDate = null,
  }: Record<string, any> = {}) {
    const qs = buildQuery({
      cursor,
      limit,
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
    });
    const payload = await request(`/api/v1/telephonic/conversations${qs}`, {
      method: 'GET',
    });
    return {
      conversations: payload.data || [],
      nextCursor: payload.pagination?.nextCursor || null,
      hasMore: payload.pagination?.hasMore || false,
    };
  },

  /**
   * Full detail for one telephonic call, including its transcript
   * (`messages`). Reuses the existing company-scoped conversation-lookup
   * endpoint. Returns the conversation object or throws on 404/permission.
   */
  async getTelephonicConversation(callId: string) {
    if (!callId) throw new Error('callId is required');
    const payload = await request(
      `/api/v1/conversation?callId=${encodeURIComponent(callId)}`,
      {
        method: 'GET',
        // PhoneSessionView's backend-fallback transcript polls this every 3s
        // during a live call — faster than the client's GET freshness window,
        // which would stall the live transcript/end detection.
        freshForMs: 0,
      },
    );
    return payload.data;
  },
};
